import type { CrmSettingsInput, GeneralSettingsInput, RoleCreateInput, SessionPolicy, UserCreateInput } from '@stoplord/shared';
import { api } from '@/lib/api';
import type { AuditEntry, DirectoryUser, NotificationItem, Role, UserAccount } from './types';

type Q = Record<string, string | number | undefined>;

export const accessApi = {
  roles: () => api.get<Role[]>('/roles'),
  catalog: () => api.get<{ key: string; module: string; label: string; scoped?: boolean; readOnly?: boolean; sensitive?: boolean }[]>('/roles/permissions'),
  createRole: (b: RoleCreateInput) => api.post<Role>('/roles', b),
  updateRole: (id: string, b: Partial<RoleCreateInput>) => api.patch<Role>(`/roles/${id}`, b),
  deleteRole: (id: string) => api.delete(`/roles/${id}`),
  users: (q: Q) => api.page<UserAccount>('/users', q),
  directory: (q?: string) => api.get<DirectoryUser[]>('/users/directory', { q }),
  createUser: (b: UserCreateInput) => api.post<UserAccount>('/users', b),
  updateAccess: (id: string, b: { roleIds?: string[]; overrides?: { key: string; scope: string; effect: string }[] }) => api.patch(`/users/${id}/access`, b),
  setStatus: (id: string, status: string, reason?: string) => api.patch(`/users/${id}/status`, { status, reason }),
  resetPassword: (id: string, temporaryPassword: string) => api.post(`/users/${id}/reset-password`, { temporaryPassword }),
  sessions: (id: string) => api.get<{ _id: string; status: string; device: { browser: string; os: string; device: string }; ip?: string; createdAt: string; lastSeenAt: string; revokeReason?: string }[]>(`/users/${id}/sessions`),
  forceLogout: (id: string) => api.post<{ revoked: number }>(`/users/${id}/force-logout`, {}),
};

export interface TenantSettingsResponse {
  name: string;
  slug: string;
  industry: string;
  status: string;
  contact: { email?: string | null; phone?: string | null; website?: string | null };
  settings: {
    timezone: string;
    currency: string;
    dateFormat: string;
    locale: string;
    fiscalYearStartMonth: number;
    sessionPolicy: SessionPolicy;
    workingHours: { workingDays: number[]; start: string; end: string; lateGraceMinutes: number };
    passwordPolicy: { minLength: number; maxFailedAttempts: number; lockoutMinutes: number };
    leadStages: { key: string; label: string; color: string; order: number; isWon?: boolean; isLost?: boolean }[];
    leadSources: { key: string; label: string }[];
    leadAssignment: { mode: 'manual' | 'round_robin'; userIds: string[] };
    terminology: Record<'client' | 'lead' | 'employee' | 'task', { singular: string; plural: string }>;
    taskCategories: string[];
  };
}

export const settingsApi = {
  get: () => api.get<TenantSettingsResponse>('/settings'),
  general: (b: GeneralSettingsInput) => api.patch('/settings/general', b),
  sessionPolicy: (b: SessionPolicy) => api.patch('/settings/session-policy', b),
  passwordPolicy: (b: { minLength: number; maxFailedAttempts: number; lockoutMinutes: number }) => api.patch('/settings/password-policy', b),
  workingHours: (b: { workingDays: number[]; start: string; end: string; lateGraceMinutes: number }) => api.patch('/settings/working-hours', b),
  terminology: (b: TenantSettingsResponse['settings']['terminology']) => api.patch('/settings/terminology', b),
  crm: (b: CrmSettingsInput) => api.patch('/settings/crm', b),
};

export const notificationsApi = {
  list: (q: Q) => api.page<NotificationItem>('/notifications', q),
  unread: () => api.get<{ count: number }>('/notifications/unread-count'),
  read: (id: string) => api.post(`/notifications/${id}/read`),
  readAll: () => api.post('/notifications/read-all'),
};

export const auditApi = { list: (q: Q) => api.page<AuditEntry>('/audit-logs', q) };

export const searchApi = {
  search: (q: string) => api.get<{ results: { type: string; id: string; title: string; subtitle?: string; link: string }[] }>('/search', { q }),
};

export const dashboardApi = {
  overview: (q: Q) => api.get<Record<string, unknown>>('/dashboard/overview', q),
  me: () => api.get<Record<string, unknown>>('/dashboard/me'),
};
